import React, { useRef } from 'react';
import Helmet from 'react-helmet';
import { Link, Route } from 'react-router-dom';
import MaterialTable from 'material-table';
import moment from 'moment';
import { useApolloClient } from '@apollo/react-hooks';
import { GET_SUBS, UPDATE_SUB } from 'graphql/admin/subscriptions';
import { getFullName } from 'utils';
import useSnack from 'hooks/useSnack';
import AddSubscription from './AddSubscription';
import { useConfirm } from 'material-ui-confirm';


const Subscriptions = ({ history }) => {
  const client = useApolloClient();
  const snack = useSnack();
  const confirm = useConfirm();
  const tableRef = useRef();
  const onRowUpdate = async (newVal, oldVal) => {
    if (newVal.end_date && newVal.end_date !== oldVal.end_date) {
      try {
        const update = {
          end_date: newVal.end_date,
        };
        if (moment(newVal.end_date).isBefore(moment(), 'date')) {
          update.status = 'expired';
        } else if (moment(newVal.end_date).isAfter(moment(), 'date')) {
          update.status = 'active';
        }
        const resp = await client.mutate({
          mutation: UPDATE_SUB,
          variables: {
            id: newVal.id,
            data: update,
          },
        });
        snack.success('Subscription updated');
        return resp.data.row;
      } catch (e) {
        snack.error(e.message);
      }
    }
  };

  const onCancel = (row) => {
    return confirm({
      title: 'Cancel Subscription',
      description: 'Are you sure? Make sure to cancel any Stripe subscriptions for this student.'
    }).then(
      async () => {
        await client.mutate({
          mutation: UPDATE_SUB,
          variables: {
            id: row.id,
            data: {
              cancelled_at: moment.utc().toJSON(),
              status: 'expired'
            },
          },
        });
        snack.success('Subscription cancelled');
        tableRef.current.onQueryChange();
      }
    )
  }

  const columns = [
    { title: 'ID', field: 'id', editable: 'never' },
    {
      title: 'Status',
      field: 'status',
      editable: 'never',
      render: (row) => {
        if (row.status === 'expired' && row.cancelled_at)
          return `Cancelled on ${moment(row.cancelled_at).format('L')}`;
        return row.status;
      },
    },
    {
      title: 'Student',
      field: 'student',
      render: (row) => {
        return (
          <Link to={`/students/${row.student.user_id}`}>
            {getFullName(row.student)}
          </Link>
        );
      },
      editable: 'never',
    },
    {
      title: 'Product',
      field: 'product.package_name',
      editable: 'never',
    },
    {
      title: 'Grad Year',
      field: 'product.graduation_year',
      editable: 'never',
    },
    {
      title: 'Type',
      field: 'product.product_type',
      editable: 'never',
    },
    {
      title: 'Start date',
      field: 'start_date',
      type: 'date',
      editable: 'never',
    },
    {
      title: 'End date',
      field: 'end_date',
      type: 'date',
    },
  ];

  return (
    <>
      <Helmet title="Subscriptions" />
      <MaterialTable
        tableRef={tableRef}
        columns={columns}
        data={async (query, p) => {
          const resp = await client.query({
            query: GET_SUBS,
            variables: {
              offset: query.page * 10,
              query: query.search ? `%${query.search}%` : undefined,
            },
          });
          const { data } = resp;
          const totalCount = data.count.aggregate.count;

          return {
            data: data.subscriptions,
            page: query.page,
            totalCount,
          };
        }}
        options={{
          pageSize: 10,
          pageSizeOptions: [],
        }}
        actions={[
          {
            icon: 'add',
            tooltip: 'Add Subscription',
            isFreeAction: true,
            onClick: (event) => history.push('/subscriptions/add'),
          },
          (row) => row.status === 'active' && (
            {
              icon: 'close',
              tooltip: 'Cancel Subscription',
              onClick: (e, row) => onCancel(row)
            }
          ),
        ]}
        editable={{
          onRowUpdate: onRowUpdate,
        }}
      />
      <Route exct path="/subscriptions/add" component={AddSubscription} />
    </>
  );
};

export default Subscriptions;
